import axios from "@/utils/axios";
import type {
  ApiInsolvenciesResponseType,
  ApiInsolvencyResponseType,
  GetInsolvenciesFilters,
  InsolvencyAPIPayload,
} from "@/types/entities/insolvencies";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

// Simple property type for temporary use until properties api integration is complete
type SimpleProperty = {
  id: string;
  name: string;
};

type ApiPropertiesResponseType = {
  data: SimpleProperty[];
  meta?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  links?: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
};

/**
 * Get paginated list of insolvencies for a specific user
 * @param userId - The user ID to filter insolvencies
 * @param filters - Additional filters (pagination, search, sort, etc.)
 */
export const useGetInsolvencies = (
  userId: string,
  insolvencyable_ids?: string[],
  filters: Partial<GetInsolvenciesFilters> = {},
) => {
  let insolvencyFilter = {};
  if (insolvencyable_ids?.length && insolvencyable_ids.length > 0) {
    insolvencyFilter = { insolvencyable_id: insolvencyable_ids.join(",") };
  } else if (userId) {
    insolvencyFilter = { applicant_id: userId };
  }
  const queryParams = {
    per_page: filters.per_page || 50,
    filter: {
      ...filters,
      ...insolvencyFilter,
    },
  };

  return useQuery({
    queryKey: ["insolvencies", queryParams],
    queryFn: async (): Promise<ApiInsolvenciesResponseType> => {
      const response = await axios.get("/admin/v1/insolvencies", {
        params: {
          ...queryParams,
          relationships: "insolvencyable,comments,comments.creator",
        },
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
    enabled:
      !!userId || (!!insolvencyable_ids && insolvencyable_ids.length > 0),
    staleTime: 30_000,
  });
};

/**
 * Get paginated list of insolvencies with infinite scroll support
 * @param userId - The user ID to filter insolvencies
 * @param filters - Additional filters (pagination, search, sort, etc.)
 */
export const useGetInsolvenciesInfinite = (
  userId: string,
  expedientsId?: string[],
  filters: Partial<GetInsolvenciesFilters> = {},
  enabled: boolean = true,
  refetchOnWindowFocus: boolean = true,
) => {
  let insolvencyFilter = {};
  if (expedientsId?.length && expedientsId.length > 0) {
    insolvencyFilter = { insolvencyable_id: expedientsId.join(",") };
  } else if (userId) {
    insolvencyFilter = { applicant_id: userId };
  }

  const queryParams = {
    per_page: filters.per_page || 10,
    filter: {
      ...filters,
      ...insolvencyFilter,
    },
  };

  return useInfiniteQuery({
    queryKey: ["insolvencies-infinite", queryParams],
    queryFn: async ({
      pageParam = 1,
    }): Promise<ApiInsolvenciesResponseType> => {
      const response = await axios.get("/admin/v1/insolvencies", {
        params: {
          ...queryParams,
          page: pageParam,
          relationships: "insolvencyable,comments,comments.creator",
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = lastPage.meta;
      if (current_page < last_page) {
        return current_page + 1;
      }
      return undefined;
    },
    enabled:
      enabled &&
      (!!userId ||
        (!!expedientsId && expedientsId.length > 0) ||
        !!filters.insolvencyable_id),
    initialPageParam: 1,
    refetchOnWindowFocus: refetchOnWindowFocus,
  });
};

/**
 * Get a single insolvency by ID
 * @param id - The insolvency ID
 */
export const useGetInsolvency = (id: string) => {
  return useQuery({
    queryKey: ["insolvency", id],
    queryFn: async (): Promise<ApiInsolvencyResponseType> => {
      const response = await axios.get(`/admin/v1/insolvencies/${id}`);
      return response.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

/**
 * Create a new insolvency
 */
export const useCreateInsolvency = (registryId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: InsolvencyAPIPayload) => {
      const response = await axios.post("/admin/v1/insolvencies", data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate insolvencies list for the specific user
      queryClient.invalidateQueries({
        queryKey: ["insolvencies", variables.insolvencyable_id],
      });
      // Also invalidate all insolvencies queries to be safe
      queryClient.invalidateQueries({
        queryKey: ["insolvencies"],
      });
      queryClient.invalidateQueries({
        queryKey: ["insolvencies-infinite"],
      });
      if (registryId) {
        // refresh the entity registry count if applicable
        queryClient.invalidateQueries({
          queryKey: ["registry-detail", registryId],
        });
      }
    },
  });
};

/**
 * Update an existing insolvency
 * @param id - The insolvency ID to update
 */
export const useUpdateInsolvency = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<InsolvencyAPIPayload>) => {
      const response = await axios.patch(`/admin/v1/insolvencies/${id}`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate the specific insolvency
      queryClient.invalidateQueries({
        queryKey: ["insolvency", id],
      });
      // Invalidate insolvencies list for the specific user
      queryClient.invalidateQueries({
        queryKey: ["insolvencies", variables.insolvencyable_id],
      });
      // Also invalidate all insolvencies queries
      queryClient.invalidateQueries({
        queryKey: ["insolvencies"],
      });
      queryClient.invalidateQueries({
        queryKey: ["insolvencies-infinite"],
      });
    },
  });
};

/**
 * Soft delete (deactivate) an insolvency by changing its status
 * @param id - The insolvency ID to deactivate
 */
export const useDeactivateInsolvency = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data?: {
      status?: string;
      comments?: string;
      reason?: string;
    }) => {
      const response = await axios.patch(`/admin/v1/insolvencies/${id}`, {
        status: data?.status || "CANCELLED",
        comments: data?.comments,
        reason: data?.reason || "",
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the specific insolvency
      queryClient.invalidateQueries({
        queryKey: ["insolvency", id],
      });
      // Invalidate all insolvencies queries
      queryClient.invalidateQueries({
        queryKey: ["insolvencies"],
      });
      queryClient.invalidateQueries({
        queryKey: ["insolvencies-infinite"],
      });
      queryClient.invalidateQueries({
        queryKey: ["registry-detail"],
      });
    },
  });
};

/**
 * Cancel an insolvency
 * @param id - The insolvency ID to cancel
 */
export const useResolveInsolvency = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await axios.patch(`/admin/v1/insolvencies/${id}`, {
        status: "CANCELLED",
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the specific insolvency
      queryClient.invalidateQueries({
        queryKey: ["insolvency", id],
      });
      // Invalidate all insolvencies queries
      queryClient.invalidateQueries({
        queryKey: ["insolvencies"],
      });
    },
  });
};

/**
 * Hard delete an insolvency (if needed in the future)
 * @param id - The insolvency ID to delete
 */
export const useDeleteInsolvency = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await axios.delete(`/admin/v1/insolvencies/${id}`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all insolvencies queries
      queryClient.invalidateQueries({
        queryKey: ["insolvencies"],
      });
    },
  });
};

/**
 * Reject an appeal for an insolvency (internal user action)
 * @param id - The insolvency ID whose appeal is being rejected
 */
export const useRejectAppeal = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { reason: string; comments: string }) => {
      const response = await axios.patch(`/admin/v1/insolvencies/${id}`, {
        status: "APPEAL_REJECTED",
        reason: data.reason,
        comments: data.comments,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insolvency", id] });
      queryClient.invalidateQueries({ queryKey: ["insolvencies"] });
      queryClient.invalidateQueries({ queryKey: ["insolvencies-infinite"] });
    },
  });
};

/**
 * Download an appeal document
 * @param fileId - The file ID to download
 * @param fileName - The name of the file
 */
export const downloadAppealDocument = async (
  fileId: string,
  fileName: string,
): Promise<boolean> => {
  try {
    const response = await axios.get(`/admin/v1/download?file=${fileId}`, {
      responseType: "blob",
    });

    // Create a temporary URL for the blob
    const url = globalThis.window.URL.createObjectURL(
      new Blob([response.data]),
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();

    // Cleanup
    link.remove();
    globalThis.window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error("Error downloading file:", error);
    // Fallback: open the download URL in a new tab
    try {
      const fallbackUrl = `/admin/v1/download?file=${fileId}`;
      window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      return false;
    } catch (openError) {
      console.error("Error opening file in new tab:", openError);
      throw error;
    }
  }
};

/**
 * Download an appeal document by URL with axios blob and fallback to opening URL
 */
export const downloadAppealDocumentByUrl = async (
  fileUrl: string,
  fileName: string,
): Promise<boolean> => {
  try {
    const response = await axios.get(fileUrl, {
      responseType: "blob",
    });

    const url = globalThis.window.URL.createObjectURL(
      new Blob([response.data]),
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();

    link.remove();
    globalThis.window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error("Error downloading/opening file via axios:", error);
    try {
      globalThis.window.open(fileUrl, "_blank", "noopener,noreferrer");
      return false;
    } catch (openError) {
      console.error("Error opening file in new tab:", openError);
      throw error;
    }
  }
};

/**
 * Get properties associated with an applicant
 * @param applicantId - The applicant ID
 */
export const useGetApplicantProperties = (applicantId: string) => {
  return useQuery({
    queryKey: ["applicant-properties", applicantId],
    queryFn: async (): Promise<ApiPropertiesResponseType> => {
      const response = await axios.get(
        `/admin/v1/applicant/${applicantId}/properties`,
      );
      return response.data;
    },
    enabled: !!applicantId,
    refetchOnWindowFocus: false,
  });
};
